/* eslint-disable no-plusplus */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Paper, Typography } from '@material-ui/core';
import dayjs from 'dayjs';

import Loading from '../../../../components/CustomComponents/Loading';
import KPTextform from '../../../../components/KPTComponents/KPTextform';

import { formActions } from '../../../../actions';
import { ACHIEVEMENT_TYPE, KEYS, MONOGRAPHS_CONSTANTS, REPORTS_CONSTANTS, SECONDARY_COLOR } from '../../../../constants';
import { KVC2026_TYPICAL_WORK_FIELDS } from '../../../../constants/kvc2026.config';
import { capitalizeFirstLetter } from '../../../../utils';
import { isDomesticQcvCode } from '../../../../utils/qcvScoreOptions';

// B. Thành tích khoa học — 7 tiểu mục (định lượng), điểm mỗi mục = tổng điểm
// các thành tích đã chấm của mục đó.
const KVC_SCIENCE_TYPES = [
  { code: '1.1', title: 'Bài báo tạp chí quốc tế', key: ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH + KEYS.INTERNATIONAL },
  { code: '1.2', title: 'Bài báo kỷ yếu hội thảo quốc tế', key: ACHIEVEMENT_TYPE.SCIENTIFIC_REPORT + KEYS.INTERNATIONAL },
  { code: '1.3', title: 'Sách (NXB uy tín thế giới)', key: ACHIEVEMENT_TYPE.MONOGRAPH + KEYS.INTERNATIONAL },
  { code: '2.1', title: 'Bài báo tạp chí trong nước', key: ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH + KEYS.DOMESTIC },
  { code: '2.2', title: 'Báo cáo hội thảo cấp ngành/toàn quốc', key: ACHIEVEMENT_TYPE.SCIENTIFIC_REPORT + KEYS.DOMESTIC },
  { code: '2.3', title: 'Sách (NXB uy tín trong nước)', key: ACHIEVEMENT_TYPE.MONOGRAPH + KEYS.DOMESTIC },
  { code: '2.4', title: 'Chương trình, dự án, đề tài NCKH', key: ACHIEVEMENT_TYPE.RESEARCH_PROJECT },
];

export default function HoSoNote() {
  const dispatch = useDispatch();

  const role = useSelector(state => state.userReducer.user?.role);
  const userId = useSelector(state => state.userReducer.user?.id);
  const loading = useSelector(state => state.formReducer.loading);
  const focusForm = useSelector(state => state.formReducer.focusForm);
  const scoreInformationList = useSelector(state => state.scoreInformationReducer.scoreInformationList);

  const [values, setValues] = useState({});

  const arrayPath = window.location.href.split('/');
  const urlId = parseInt(arrayPath.slice(arrayPath.length - 1));

  const applicationDetail = useMemo(() => {
    const detail = { ...focusForm };
    if (!focusForm) return null;
    Object.keys(ACHIEVEMENT_TYPE).forEach((key) => {
      const _key = 'form' + capitalizeFirstLetter(ACHIEVEMENT_TYPE[key]);
      detail[ACHIEVEMENT_TYPE[key]] = (detail[_key] || [])
        .map((e) => ({
          ...e[ACHIEVEMENT_TYPE[key]],
          note: e.note,
          score: e.score,
          accepted: e.accepted,
          [`${_key}Id`]: e.id,
          thanhTichAccepted: e.accepted,
        }))
        .sort((a, b) => a.id - b.id);
    });

    // Backend "report" không lưu filterType -> domestic = "không phải quốc tế".
    // Ưu tiên nhận diện code MỚI qua qcv_score_infos, fallback mảng type cũ.
    const isDomKvc = (value, ...mucPrefixes) => {
      for (const muc of mucPrefixes) {
        const r = isDomesticQcvCode(scoreInformationList, muc, value, "");
        if (r !== null) return r;
      }
      return null;
    };

    detail[ACHIEVEMENT_TYPE.SCIENTIFIC_REPORT + KEYS.DOMESTIC] = detail[ACHIEVEMENT_TYPE.SCIENTIFIC_REPORT].filter((e) => {
      const isDom = isDomKvc(e.type, "1.2");
      if (isDom !== null) return isDom;
      return e.filterType === KEYS.DOMESTIC
        || (!e.filterType && !REPORTS_CONSTANTS.TYPE_OPTIONS.INTERNATIONAL.find((o) => e.type === o.value));
    });
    detail[ACHIEVEMENT_TYPE.SCIENTIFIC_REPORT + KEYS.INTERNATIONAL] = detail[ACHIEVEMENT_TYPE.SCIENTIFIC_REPORT].filter((e) => {
      const isDom = isDomKvc(e.type, "1.2");
      if (isDom !== null) return !isDom;
      return REPORTS_CONSTANTS.TYPE_OPTIONS.INTERNATIONAL.find((o) => e.type === o.value);
    });
    detail[ACHIEVEMENT_TYPE.MONOGRAPH + KEYS.DOMESTIC] = detail[ACHIEVEMENT_TYPE.MONOGRAPH].filter((e) => {
      const isDom = isDomKvc(e.type, "2.3", "1.3");
      if (isDom !== null) return isDom;
      return MONOGRAPHS_CONSTANTS.TYPE_OPTIONS.DOMESTIC.find((o) => e.type === o.value);
    });
    detail[ACHIEVEMENT_TYPE.MONOGRAPH + KEYS.INTERNATIONAL] = detail[ACHIEVEMENT_TYPE.MONOGRAPH].filter((e) => {
      const isDom = isDomKvc(e.type, "2.3", "1.3");
      if (isDom !== null) return !isDom;
      return MONOGRAPHS_CONSTANTS.TYPE_OPTIONS.INTERNATIONAL.find((o) => o.value === e.type);
    });
    detail[ACHIEVEMENT_TYPE.RESEARCH_PROJECT] = detail[ACHIEVEMENT_TYPE.RESEARCH_PROJECT].map((e) => ({
      ...e,
      start_at: dayjs(e.start_at).format('DD/MM/YYYY'),
      finish_at: dayjs(e.finish_at).format('DD/MM/YYYY'),
    }));
    detail[ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH + KEYS.DOMESTIC] = detail[ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH].filter((e) => e.filterType === KEYS.DOMESTIC);
    detail[ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH + KEYS.INTERNATIONAL] = detail[ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH].filter((e) => e.filterType === KEYS.INTERNATIONAL);
    return detail;
  }, [focusForm]);

  const safeParse = (str, fallback) => {
    try { return JSON.parse(str) ?? fallback; } catch (e) { return fallback; }
  };

  // Điểm định lượng từng tiểu mục B (tự động = tổng điểm thành tích đã chấm).
  const scienceByType = useMemo(() => {
    if (!applicationDetail) return [];
    const round = (v) => Math.round((v || 0) * 100) / 100;
    return KVC_SCIENCE_TYPES.map((t) => ({
      ...t,
      score: round((applicationDetail[t.key] || []).reduce((s, e) => s + (e?.score || 0), 0)),
    }));
  }, [applicationDetail]);

  const scienceTotal = useMemo(
    () => Math.round(scienceByType.reduce((s, t) => s + t.score, 0) * 100) / 100,
    [scienceByType]
  );

  useEffect(() => {
    if (!applicationDetail) return;
    const tw = safeParse(applicationDetail.typicalWork, {}) || {};
    setValues((prev) => ({
      ...prev,
      note: applicationDetail?.note,
      // A. Công trình tiêu biểu — 4 tiêu chí (điểm định tính)
      tw1: tw.tw1 ?? '',
      tw2: tw.tw2 ?? '',
      tw3: tw.tw3 ?? '',
      tw4: tw.tw4 ?? '',
      // scoreD cũ (định tính) — dùng làm fallback nếu chưa chấm theo tiêu chí
      scoreDLegacy: Math.round((applicationDetail?.scoreD || 0) * 100) / 100,
      // C. Khen thưởng + hoạt động cộng đồng (giám khảo chấm)
      scoreB: Math.round((applicationDetail?.scoreB || 0) * 100) / 100,
      scoreC: Math.round((applicationDetail?.scoreC || 0) * 100) / 100,
    }));
    // eslint-disable-next-line
  }, [applicationDetail]);

  // A: tổng 4 tiêu chí (cap 50). Nếu chưa chấm tiêu chí nào -> dùng điểm cũ.
  const cap = (v, max) => Math.min(Math.max(Number(v) || 0, 0), max);
  const twScores = [
    cap(values?.tw1, KVC2026_TYPICAL_WORK_FIELDS[0].maxPoint),
    cap(values?.tw2, KVC2026_TYPICAL_WORK_FIELDS[1].maxPoint),
    cap(values?.tw3, KVC2026_TYPICAL_WORK_FIELDS[2].maxPoint),
    cap(values?.tw4, KVC2026_TYPICAL_WORK_FIELDS[3].maxPoint),
  ];
  const hasTw = [values?.tw1, values?.tw2, values?.tw3, values?.tw4].some(
    (v) => v !== '' && v !== undefined && v !== null
  );
  const scoreD = hasTw
    ? Math.min(twScores.reduce((s, v) => s + v, 0), 50)
    : (Number(values?.scoreDLegacy) || 0);

  const buildPayload = (status) => ({
    ...values,
    scoreA: scienceTotal,
    scoreD,
    typicalWork: JSON.stringify({
      tw1: twScores[0], tw2: twScores[1], tw3: twScores[2], tw4: twScores[3],
    }),
    id: urlId,
    status,
  });
  const saveNote = () => dispatch(formActions.updateBCD(buildPayload('need_update')));
  const handleSubmit = () => dispatch(formActions.updateBCD(buildPayload('approved')));

  const sectionLabel = (text, note) => (
    <Box style={{ marginTop: 8 }}>
      <Typography variant='subtitle2'><b>{text}</b></Typography>
      {note ? <Typography variant='body2' style={{ color: '#666' }}>{note}</Typography> : null}
    </Box>
  );

  var textForm = [
    // ===== A. CÔNG TRÌNH TIÊU BIỂU =====
    { key: 'a-label', width: 12, value: true, render: sectionLabel('A. Công trình tiêu biểu (định tính — tối đa 50 điểm)', 'Chấm chi tiết theo 4 tiêu chí:') },
    { key: 'tw1', width: 12, label: `1) ${KVC2026_TYPICAL_WORK_FIELDS[0].label} (0-${KVC2026_TYPICAL_WORK_FIELDS[0].maxPoint})`, type: 'float', value: values?.tw1 },
    { key: 'tw2', width: 12, label: `2) ${KVC2026_TYPICAL_WORK_FIELDS[1].label} (0-${KVC2026_TYPICAL_WORK_FIELDS[1].maxPoint})`, type: 'float', value: values?.tw2 },
    { key: 'tw3', width: 12, label: `3) ${KVC2026_TYPICAL_WORK_FIELDS[2].label} (0-${KVC2026_TYPICAL_WORK_FIELDS[2].maxPoint})`, type: 'float', value: values?.tw3 },
    { key: 'tw4', width: 12, label: `4) ${KVC2026_TYPICAL_WORK_FIELDS[3].label} (0-${KVC2026_TYPICAL_WORK_FIELDS[3].maxPoint})`, type: 'float', value: values?.tw4 },
    { key: 'a-sum', width: 12, value: true, render: (
      <Typography variant='body2' style={{ textAlign: 'right' }}>
        Tổng điểm mục A: <b>{scoreD}</b> / 50
      </Typography>
    ) },
    // ===== B. THÀNH TÍCH KHOA HỌC =====
    { key: 'b-label', width: 12, value: true, render: sectionLabel('B. Thành tích khoa học (định lượng — tối đa 50 điểm)', 'Điểm tự động từng tiểu mục (tổng điểm các thành tích đã chấm):') },
    { key: 'b-types', width: 12, value: true, render: (
      <Box style={{ marginBottom: 4 }}>
        {scienceByType.map((t) => (
          <Box key={t.code} display='flex' justifyContent='space-between' alignItems='center' style={{ padding: '3px 0', borderBottom: '1px dashed #eee' }}>
            <Typography variant='body2' style={{ paddingRight: 8 }}>{`${t.code}. ${t.title}`}</Typography>
            <Typography variant='body2'><b>{t.score}</b></Typography>
          </Box>
        ))}
        <Box display='flex' justifyContent='space-between' style={{ padding: '4px 0' }}>
          <Typography variant='body2'><b>Tổng điểm mục B (tự động)</b></Typography>
          <Typography variant='body2'><b>{scienceTotal}</b></Typography>
        </Box>
      </Box>
    ) },
    // ===== C. KHEN THƯỞNG & HOẠT ĐỘNG CỘNG ĐỒNG =====
    { key: 'c-label', width: 12, value: true, render: sectionLabel('C. Thành tích khen thưởng & hoạt động cộng đồng') },
    {
      key: 'scoreB', width: 12, label: '1. Thành tích khen thưởng (tối đa 10 điểm)', type: 'float', value: values?.scoreB,
      note: (
        <>
          <Box><b>1.1: </b>Khen thưởng cấp <b>Nhà nước</b> (tối đa <b>10 điểm</b>)</Box>
          <Box><b>1.2: </b>Bằng khen <b>Bộ, ban, ngành, tỉnh</b> (tối đa <b>05 điểm</b>)</Box>
        </>
      ),
    },
    {
      key: 'scoreC', width: 12, label: '2. Hoạt động cộng đồng (tối đa 5 điểm)', type: 'float', value: values?.scoreC,
      note: (
        <>
          <Box><b>2.1: </b>Chia sẻ tri thức, tập huấn, đào tạo... trong ngành/liên ngành xét giải (tối đa <b>3 điểm</b>)</Box>
          <Box><b>2.2: </b>Tình nguyện vì cộng đồng nói chung (tối đa <b>2 điểm</b>)</Box>
        </>
      ),
    },
    { key: 'note', width: 12, height: 5, label: 'Ghi chú của ban thư ký', value: values?.note },
    { key: 'button-2', width: 6, label: 'Không duyệt hồ sơ', type: 'button', onClick: saveNote },
    { key: 'submit', width: 6, label: 'Duyệt hồ sơ', type: 'submit' },
  ];

  if ((applicationDetail && !role) || role === 'candidate') {
    if (applicationDetail?.status !== 'need_update' || applicationDetail?.profile?.id !== userId) return null;
    textForm = [{ key: 'note', width: 12, height: 10, value: values?.note, disabled: true }];
  }

  const round = (v) => Math.round((v || 0) * 100) / 100;
  const breakdownRows = [
    { label: 'A. Công trình tiêu biểu', value: scoreD, cap: 50 },
    { label: 'B. Thành tích khoa học', value: round(scienceTotal), cap: 50 },
    { label: 'C. Thành tích khen thưởng', value: round(values?.scoreB), cap: 10 },
    { label: 'D. Hoạt động cộng đồng', value: round(values?.scoreC), cap: 5 },
  ];
  const score = round(breakdownRows.reduce((s, r) => s + (r.value || 0), 0));

  return (
    <Paper style={{ padding: 12 }}>
      <Typography variant='h6' style={{ textAlign: 'center', fontWeight: 700 }}>
        CHẤM ĐIỂM HỒ SƠ
      </Typography>
      <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' style={{ padding: '8px 0' }}>
        <Typography variant='h2' style={{ color: SECONDARY_COLOR }}>
          <b>{score}</b>
        </Typography>
        <Typography variant='subtitle1'>Tổng điểm của ứng viên</Typography>
      </Box>
      <Box width='100%' style={{ marginBottom: 12 }}>
        {breakdownRows.map((row) => (
          <Box key={row.label} display='flex' justifyContent='space-between' alignItems='center' style={{ padding: '6px 0', borderBottom: '1px solid #eee' }}>
            <Typography variant='body2'>{row.label}</Typography>
            <Typography variant='body2'>
              <b>{round(row.value)}</b>
              <span style={{ color: '#999' }}>{` / ${row.cap}`}</span>
            </Typography>
          </Box>
        ))}
        <Box display='flex' justifyContent='space-between' style={{ padding: '8px 0' }}>
          <Typography variant='subtitle1'><b>Tổng điểm</b></Typography>
          <Typography variant='subtitle1' style={{ color: SECONDARY_COLOR }}><b>{score}</b></Typography>
        </Box>
      </Box>
      <KPTextform
        textForm={textForm}
        handleSubmit={handleSubmit}
        values={values}
        setValues={setValues}
      />
      <Loading open={loading} />
    </Paper>
  );
}
